import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import FeedbackModal from '../components/UIElements/FeedbackModal/FeedbackModal';

const FeedbackModalContext = createContext(null);

export const FeedbackModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    open: false,
    type: 'success',
    title: '',
    description: '',
    detail: null,
    confirmText: '',
    cancelText: 'Cancelar',
    showCancel: false,
    disableBackdropClick: true,
    confirmButtonColor: undefined,
    confirmButtonHoverColor: undefined,
    confirmButtonActiveColor: undefined,
    ringBadge: undefined,
    maxWidth: undefined,
    centerButton: false,
    notificationBanner: null,
  });

  const resolverRef = useRef(null);

  const showModal = useCallback((options) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setModalState({
        open: true,
        type: options.type || 'success',
        title: options.title || '',
        description: options.description || '',
        detail: options.detail || null,
        confirmText: options.confirmText,
        cancelText: options.cancelText || 'Cancelar',
        showCancel: Boolean(options.showCancel),
        stackedButtons: Boolean(options.stackedButtons),
        disableBackdropClick: options.disableBackdropClick !== false,
        confirmButtonColor: options.confirmButtonColor,
        confirmButtonHoverColor: options.confirmButtonHoverColor,
        confirmButtonActiveColor: options.confirmButtonActiveColor,
        ringBadge: options.ringBadge,
        maxWidth: options.maxWidth,
        centerButton: Boolean(options.centerButton),
        notificationBanner: options.notificationBanner || null,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  }, []);

  const showSuccess = useCallback(
    (options) =>
      showModal({
        ...options,
        type: 'success',
        showCancel: false,
      }),
    [showModal]
  );

  const showError = useCallback(
    (options) =>
      showModal({
        ...options,
        type: 'error',
        showCancel: false,
      }),
    [showModal]
  );

  const showWarning = useCallback(
    (options) =>
      showModal({
        ...options,
        type: 'warning',
        showCancel: false,
      }),
    [showModal]
  );

  const showConfirm = useCallback(
    (options) =>
      showModal({
        ...options,
        type: options.type || 'warning',
        showCancel: true,
      }),
    [showModal]
  );

  const showQuestion = useCallback(
    (options) =>
      showModal({
        confirmButtonColor: '#00a651',
        confirmButtonHoverColor: '#008e45',
        confirmButtonActiveColor: '#007a3b',
        ringBadge: true,
        ...options,
        type: 'question',
        showCancel: true,
      }),
    [showModal]
  );

  const showDelete = useCallback(
    (options) =>
      showModal({
        confirmText: 'ELIMINAR',
        cancelText: 'CANCELAR',
        ...options,
        type: 'delete',
        showCancel: true,
        stackedButtons: true,
      }),
    [showModal]
  );

  const showInfo = useCallback(
    (options) =>
      showModal({
        ...options,
        type: 'info',
        showCancel: false,
      }),
    [showModal]
  );

  const closeModal = useCallback(() => {
    handleCancel();
  }, [handleCancel]);

  return (
    <FeedbackModalContext.Provider
      value={{
        showModal,
        showSuccess,
        showError,
        showWarning,
        showConfirm,
        showQuestion,
        showDelete,
        showInfo,
        closeModal,
      }}
    >
      {children}
      <FeedbackModal
        open={modalState.open}
        type={modalState.type}
        title={modalState.title}
        description={modalState.description}
        detail={modalState.detail}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        showCancel={modalState.showCancel}
        stackedButtons={modalState.stackedButtons}
        confirmButtonColor={modalState.confirmButtonColor}
        confirmButtonHoverColor={modalState.confirmButtonHoverColor}
        confirmButtonActiveColor={modalState.confirmButtonActiveColor}
        ringBadge={modalState.ringBadge}
        maxWidth={modalState.maxWidth}
        centerButton={modalState.centerButton}
        notificationBanner={modalState.notificationBanner}
        disableBackdropClick={modalState.disableBackdropClick}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </FeedbackModalContext.Provider>
  );
};

export const useFeedbackModalContext = () => {
  const context = useContext(FeedbackModalContext);
  if (!context) {
    throw new Error('useFeedbackModalContext debe usarse dentro de un FeedbackModalProvider');
  }
  return context;
};

export default FeedbackModalContext;
